// src/pages/Checkout.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useCart } from '../context/CartContext';
import { uploadToImageKit } from '../utils/imagekitUpload';
import { sendWhatsAppOrderConfirmation } from '../utils/whatsappSender';
import {
  ArrowLeft, ShoppingBag, User, Mail, Phone, MapPin,
  CreditCard, Truck, Check, AlertCircle, Building2, Copy,
  Upload, Image as ImageIcon, X, ShieldCheck, Package,
  Tag, Percent, Zap
} from 'lucide-react';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getSubtotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [siteSettings, setSiteSettings] = useState(null);
  const [shippingTaxSettings, setShippingTaxSettings] = useState(null);
  const [copied, setCopied] = useState('');

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', city: '',
    state: 'Punjab', postalCode: '', paymentMethod: 'cod',
    transactionId: '', senderName: '', screenshot: '',
  });

  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponMessage, setCouponMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [siteSnap, taxSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'site')),
          getDoc(doc(db, 'settings', 'shipping-tax')),
        ]);

        if (siteSnap.exists()) {
          const data = siteSnap.data();
          setSiteSettings(data);
          if (data.codEnabled === false && data.bankTransferEnabled !== false) {
            setFormData(prev => ({ ...prev, paymentMethod: 'bank' }));
          } else if (data.bankTransferEnabled === false && data.codEnabled !== false) {
            setFormData(prev => ({ ...prev, paymentMethod: 'cod' }));
          }
        }

        if (taxSnap.exists()) {
          setShippingTaxSettings(taxSnap.data());
        } else {
          setShippingTaxSettings({
            shippingEnabled: true,
            defaultShippingFee: 200,
            freeShippingEnabled: true,
            freeShippingLimit: 2000,
            taxEnabled: false,
            taxName: 'GST',
            taxRate: 0,
          });
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };
    fetchSettings();
  }, []);

  const subtotal = getSubtotal();
  const discount = appliedCoupon ? appliedCoupon.discountAmount : 0;
  const shippingEnabled = shippingTaxSettings?.shippingEnabled !== false;
  const freeShippingEnabled = shippingTaxSettings?.freeShippingEnabled !== false;
  const freeShippingLimit = shippingTaxSettings?.freeShippingLimit || 2000;
  const defaultShippingFee = shippingTaxSettings?.defaultShippingFee || 200;

  let shipping = 0;
  if (shippingEnabled && subtotal > 0) {
    if (freeShippingEnabled && subtotal >= freeShippingLimit) {
      shipping = 0;
    } else {
      shipping = defaultShippingFee;
    }
  }

  const subtotalAfterDiscount = Math.max(0, subtotal - discount);
  const taxEnabled = shippingTaxSettings?.taxEnabled === true;
  const taxRate = shippingTaxSettings?.taxRate || 0;
  const taxName = shippingTaxSettings?.taxName || 'Tax';
  const taxAmount = taxEnabled ? Math.round(((subtotalAfterDiscount + shipping) * taxRate) / 100) : 0;
  const total = subtotalAfterDiscount + shipping + taxAmount;

  const codEnabled = siteSettings?.codEnabled !== false;
  const bankEnabled = siteSettings?.bankTransferEnabled !== false;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  const handleScreenshotUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingScreenshot(true);
    try {
      const url = await uploadToImageKit(file, 'payments');
      setFormData(prev => ({ ...prev, screenshot: url }));
      setMessage({ type: 'success', text: 'Screenshot uploaded!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Screenshot upload failed.' });
    } finally {
      setUploadingScreenshot(false);
    }
  };

  const handleApplyCoupon = async () => {
    setCouponMessage({ type: '', text: '' });
    if (!couponCode.trim()) {
      setCouponMessage({ type: 'error', text: 'Coupon code daalein.' });
      return;
    }
    setCouponLoading(true);
    try {
      const q = query(collection(db, 'coupons'), where('code', '==', couponCode.trim().toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) {
        setCouponMessage({ type: 'error', text: 'Ye coupon code exist nahi karta.' });
        setCouponLoading(false);
        return;
      }
      const couponDoc = snap.docs[0];
      const coupon = { id: couponDoc.id, ...couponDoc.data() };
      if (!coupon.active) {
        setCouponMessage({ type: 'error', text: 'Ye coupon active nahi hai.' });
        setCouponLoading(false);
        return;
      }
      if (coupon.expiryDate) {
        const expiry = new Date(coupon.expiryDate);
        if (expiry < new Date()) {
          setCouponMessage({ type: 'error', text: 'Ye coupon expire ho gaya hai.' });
          setCouponLoading(false);
          return;
        }
      }
      if (coupon.usageLimit && (coupon.usedCount || 0) >= coupon.usageLimit) {
        setCouponMessage({ type: 'error', text: 'Is coupon ki usage limit khatam ho gayi.' });
        setCouponLoading(false);
        return;
      }
      if (coupon.minOrder && subtotal < coupon.minOrder) {
        setCouponMessage({ type: 'error', text: `Minimum order Rs. ${coupon.minOrder} hona chahiye.` });
        setCouponLoading(false);
        return;
      }
      let discountAmount = 0;
      if (coupon.type === 'percentage') {
        discountAmount = Math.round((subtotal * coupon.value) / 100);
        if (coupon.maxDiscount && discountAmount > coupon.maxDiscount) {
          discountAmount = coupon.maxDiscount;
        }
      } else {
        discountAmount = coupon.value;
      }
      if (discountAmount > subtotal) discountAmount = subtotal;
      setAppliedCoupon({ ...coupon, discountAmount });
      setCouponMessage({ type: 'success', text: `Coupon applied! Rs. ${discountAmount} ka discount mila.` });
    } catch (err) {
      console.error('Coupon error:', err);
      setCouponMessage({ type: 'error', text: 'Coupon apply nahi ho saka.' });
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponMessage({ type: '', text: '' });
  };

  // ✅ AUTOMATIC POSTEX BOOKING FUNCTION
  const autoBookPostEx = async (orderId, orderData) => {
    try {
      // 1. PostEx settings get karein
      const postexRef = doc(db, 'settings', 'postex');
      const postexSnap = await getDoc(postexRef);
      
      if (!postexSnap.exists() || !postexSnap.data().active) {
        console.log('PostEx is disabled or not configured');
        return;
      }

      const config = postexSnap.data();
      if (!config.apiToken) {
        console.log('PostEx API Token missing');
        return;
      }

      // 2. Order Detail format karein
      const orderDetail = orderData.items.map(item => `${item.title} x${item.quantity}`).join(', ');

      // 3. PostEx API Payload banayein
      const payload = {
        orderType: 'Normal',
        orderRefNumber: `Mr${orderId.slice(-6).toUpperCase()}`,
        orderAmount: orderData.total,
        orderDate: new Date().toISOString().split('T')[0],
        customerName: orderData.customerName,
        customerPhone: orderData.phone,
        deliveryCity: orderData.city,
        deliveryAddress: `${orderData.address}, ${orderData.state}`,
        airwayBillCopies: 1,
        items: 1,
        pickupCity: config.pickupCity || 'Rawalpindi',
        pickupAddress: config.pickupAddress || 'Adyala Road Rawalpindi',
        returnCity: config.returnCity || 'Rawalpindi',
        returnAddress: config.returnAddress || 'Adyala Road Rawalpindi',
        bookingWeight: config.bookingWeight || 0.5,
        orderDetail: orderDetail,
        notes: ''
      };

      // 4. PostEx API Call
      const response = await fetch('https://api.postex.pk/services/integration/api/order/v1/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'token': config.apiToken
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      // 5. Agar success hua toh Tracking Number save karein
      if (response.ok && data.statusCode === '200') {
        const trackingNumber = data.dist.trackingNumber;
        
        await updateDoc(doc(db, 'orders', orderId), {
          trackingNumber: trackingNumber,
          courierName: 'PostEx',
          status: 'confirmed',
          postexBookedAt: new Date()
        });
        console.log('✅ Auto Booked on PostEx. CN:', trackingNumber);
      } else {
        console.error('❌ PostEx Booking Failed:', data);
      }
    } catch (error) {
      console.error('❌ PostEx Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.city) {
      setMessage({ type: 'error', text: 'Saari zaroori fields fill karein.' });
      return;
    }

    if (formData.paymentMethod === 'bank') {
      if (!formData.transactionId || !formData.senderName || !formData.screenshot) {
        setMessage({ type: 'error', text: 'Bank Transfer ke liye Transaction ID, Sender Name, aur Screenshot zaroori hai.' });
        return;
      }
    }

    setLoading(true);

    try {
      const orderData = {
        customerName: formData.name,
        customerEmail: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentMethod === 'cod' ? 'unpaid' : 'pending-verification',
        status: 'processing',
        items: cartItems.map(item => ({
          id: item.id,
          title: item.title,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
        })),
        subtotal,
        discount,
        couponCode: appliedCoupon?.code || '',
        shipping,
        tax: taxAmount,
        taxName: taxEnabled ? taxName : '',
        taxRate: taxEnabled ? taxRate : 0,
        total,
        createdAt: new Date(),
        ...(formData.paymentMethod === 'bank' && {
          transactionId: formData.transactionId,
          senderName: formData.senderName,
          paymentScreenshot: formData.screenshot,
        }),
      };

      const docRef = await addDoc(collection(db, 'orders'), orderData);
      const newOrderId = docRef.id;

      // Increment coupon usage count
      if (appliedCoupon) {
        try {
          await updateDoc(doc(db, 'coupons', appliedCoupon.id), {
            usedCount: (appliedCoupon.usedCount || 0) + 1,
          });
        } catch (err) {}
      }

      // ✅ WhatsApp Order Confirmation (Background)
      try {
        sendWhatsAppOrderConfirmation(formData.phone, formData.name, newOrderId.slice(-8).toUpperCase(), total);
      } catch (err) {}

      // ✅ POSTEX AUTO BOOKING (Background)
      autoBookPostEx(newOrderId, orderData);

      clearCart();
      navigate(`/order-success/${newOrderId}`);
    } catch (error) {
      console.error('Order error:', error);
      setMessage({ type: 'error', text: 'Order place karne mein masla aaya.' });
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen py-12 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="rounded-2xl p-12 anim-scaleIn" style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
            <ShoppingBag size={80} className="mx-auto mb-4 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
            <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-primary-text)' }}>Your Cart is Empty</h2>
            <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold btn-2d shadow-lg" style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
              Start Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl p-5 md:p-6 anim-fadeInUp" style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
                <h2 className="text-lg font-bold mb-5 flex items-center gap-2" style={{ color: 'var(--color-primary-text)' }}>
                  <MapPin size={20} style={{ color: 'var(--color-link)' }} /> Shipping Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>Full Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full rounded-lg py-3 px-4 text-sm focus:outline-none" style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }} placeholder="Ahmed Khan" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full rounded-lg py-3 px-4 text-sm focus:outline-none" style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }} placeholder="ahmed@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>Phone *</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full rounded-lg py-3 px-4 text-sm focus:outline-none" style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }} placeholder="+92 300 1234567" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>City *</label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} required className="w-full rounded-lg py-3 px-4 text-sm focus:outline-none" style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }} placeholder="Lahore" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>Full Address *</label>
                    <textarea name="address" value={formData.address} onChange={handleChange} required rows={3} className="w-full rounded-lg py-3 px-4 text-sm focus:outline-none resize-none" style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }} placeholder="House #, Street, Area..." />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl p-5 md:p-6 anim-fadeInUp" style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
                <h2 className="text-lg font-bold mb-5 flex items-center gap-2" style={{ color: 'var(--color-primary-text)' }}>
                  <CreditCard size={20} style={{ color: 'var(--color-link)' }} /> Payment Method
                </h2>
                <div className="space-y-3">
                  {codEnabled && (
                    <label className="flex items-center gap-3 p-4 rounded-xl cursor-pointer transition" style={{ backgroundColor: formData.paymentMethod === 'cod' ? 'var(--color-body-bg)' : 'transparent', border: formData.paymentMethod === 'cod' ? `2px solid var(--color-accent)` : `1px solid var(--color-border)` }}>
                      <input type="radio" name="paymentMethod" value="cod" checked={formData.paymentMethod === 'cod'} onChange={handleChange} className="w-4 h-4" />
                      <Truck size={20} style={{ color: 'var(--color-link)' }} />
                      <div>
                        <p className="font-semibold text-sm" style={{ color: 'var(--color-primary-text)' }}>Cash on Delivery (COD)</p>
                        <p className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>Order milne par payment karein</p>
                      </div>
                    </label>
                  )}
                  {bankEnabled && (
                    <label className="flex items-center gap-3 p-4 rounded-xl cursor-pointer transition" style={{ backgroundColor: formData.paymentMethod === 'bank' ? 'var(--color-body-bg)' : 'transparent', border: formData.paymentMethod === 'bank' ? `2px solid var(--color-accent)` : `1px solid var(--color-border)` }}>
                      <input type="radio" name="paymentMethod" value="bank" checked={formData.paymentMethod === 'bank'} onChange={handleChange} className="w-4 h-4" />
                      <Building2 size={20} style={{ color: 'var(--color-link)' }} />
                      <div>
                        <p className="font-semibold text-sm" style={{ color: 'var(--color-primary-text)' }}>Bank Transfer</p>
                        <p className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>Bank/Easypaisa/JazzCash se payment</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="rounded-2xl p-5 sticky top-24 anim-fadeInRight" style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--color-primary-text)' }}>
                  <Package size={18} style={{ color: 'var(--color-link)' }} /> Order Summary
                </h2>
                <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex gap-2 items-center">
                      <img src={item.image} alt={item.title} className="w-12 h-12 object-cover rounded-lg" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold line-clamp-1" style={{ color: 'var(--color-primary-text)' }}>{item.title}</p>
                        <p className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>{item.quantity} × Rs. {item.price}</p>
                      </div>
                      <p className="text-xs font-bold" style={{ color: 'var(--color-link)' }}>Rs. {item.price * item.quantity}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-3 space-y-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="flex justify-between"><span style={{ color: 'var(--color-secondary-text)' }}>Subtotal</span><span className="font-semibold">{subtotal}</span></div>
                  <div className="flex justify-between"><span style={{ color: 'var(--color-secondary-text)' }}>Shipping</span><span className="font-semibold">{shipping === 0 ? 'FREE' : `Rs. ${shipping}`}</span></div>
                  <div className="flex justify-between"><span className="font-bold">Total</span><span className="font-bold" style={{ color: 'var(--color-link)' }}>Rs. {total}</span></div>
                </div>
                <button type="submit" disabled={loading} className="w-full mt-5 rounded-full py-4 font-bold flex items-center justify-center gap-2 btn-2d shadow-xl" style={{ backgroundColor: 'var(--color-button-bg)', color: '#FFFFFF' }}>
                  {loading ? 'Placing...' : 'Place Order'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;